/**
 * O domínio da tela de revisão de regras (`TP-050`, T9/T10/T11), em pt-BR.
 *
 * Mesma disciplina de `features/contratos/api/tipos.ts` (`TP-049`): a seção
 * `*Api` é a forma CRUA, escrita à mão a partir de
 * `contracts/openapi/anery-api.v1.json` (schemas `RuleReviewQueueItem`,
 * `RegraRevisadaResponse`, `AprovacaoEmLoteResponse`, `SourceDiff`,
 * `SourceItemDiff`, `AdjustedRuleDiff`, `EstadoDaFonteAtivadaResponse`,
 * `PaginaDaFonteResponse`), já que a geração de `@anery/api-contracts` ainda
 * não cobre este bloco neste worktree.
 */

import type { EstadoDaFonteApi, ItemApi, TipoDeFonte, TipoDeFonteApi } from '../../api/tipos';

// ------------------------------------------------------------- forma da API

export type TipoDeRegraApi =
  | 'BillingUnit'
  | 'Inclusion'
  | 'Exclusion'
  | 'PriceFormula'
  | 'Authorization'
  | 'BillingRule'
  | 'DocumentaryDeadline'
  | 'Sla'
  | 'GlosaHypothesis'
  | 'AppealDeadline'
  | 'NotClassifiable';

export type ConfiancaDaRegraApi = 'High' | 'Medium' | 'Low';

export type EstadoDaRegraApi = 'Extracted' | 'Approved' | 'Adjusted' | 'Rejected' | 'Retired';

export type MotivoDoDiffDoItemApi = 'Added' | 'Removed' | 'Changed';

export interface RegraDaFilaApi {
  readonly id: string;
  readonly sourceId: string;
  readonly sourceTypeToken: string | null;
  readonly type: TipoDeRegraApi;
  readonly confidence: ConfiancaDaRegraApi;
  readonly scope: unknown;
  readonly effect: unknown;
  readonly excerpt: string | null;
  readonly page: number;
  readonly excerptFound: boolean;
  readonly notes: string | null;
  /** Descrição de cada código que a regra cita, tirada dos itens do contrato (`TP-055`). */
  readonly itemDescriptions?: Readonly<Record<string, string>> | null;
}

export interface RegraRevisadaApi {
  readonly status: EstadoDaRegraApi;
  readonly novaRegraId: string | null;
}

export interface AprovacaoEmLoteApi {
  readonly aprovadas: number;
}

export interface EstadoDaFonteAtivadaApi {
  readonly status: EstadoDaFonteApi;
}

export interface SourceItemDiffApi {
  readonly code: string | null;
  readonly before: ItemApi | null;
  readonly after: ItemApi | null;
  readonly reason: MotivoDoDiffDoItemApi;
}

export interface AdjustedRuleDiffApi {
  readonly ruleId: string;
  readonly type: TipoDeRegraApi;
  readonly replacesRuleId: string;
}

/** `SourceReference`: o que identifica uma fonte sem depender só do nome. */
export interface ReferenciaDeFonteApi {
  readonly id: string;
  readonly type: TipoDeFonteApi;
  readonly createdAt: string;
  readonly validFrom: string;
  readonly pages: number | null;
  readonly itemCount: number;
  readonly hashPrefix: string;
}

export interface SourceDiffApi {
  readonly isFirstSource: boolean;
  readonly current?: ReferenciaDeFonteApi | null;
  readonly previous?: ReferenciaDeFonteApi | null;
  readonly items: readonly SourceItemDiffApi[] | null;
  readonly adjustedRules: readonly AdjustedRuleDiffApi[] | null;
}

/**
 * `GET /fontes/{id}/paginas-revisao` — texto mascarado de cada página (D5),
 * mais a observação (`fonte_pagina.observacao`) quando a página tiver uma.
 *
 * CORRIGIDO (`DI34`, `TP-050`): o gap registrado em `DI28` — `GET
 * /fontes/{id}/paginas` exigir chave de MÁQUINA (`ContratoCompiladorEndpoints`,
 * `ADR-0002`), que a tela de sessão de PESSOA nunca guarda, e por isso
 * respondia `401` contra a API real — foi fechado em `TP-051` com uma rota
 * de SESSÃO irmã (`ListarPaginasParaRevisaoAsync`, `ObterPaginasDaFonteParaRevisao`):
 * mesmo formato de resposta (`PaginaDaFonteResponse`, reaproveitado — `DI34`
 * do TP-051), um campo novo (`observacao`). A tela passou a chamar esta rota.
 */
export interface PaginaDaFonteApi {
  readonly pagina: number;
  readonly texto: string;
  readonly mascarado: boolean;
  readonly secao: string | null;
  readonly tipoConteudo: readonly string[];
  readonly clausulasIniciam: readonly string[];
  readonly clausulasTerminam: readonly string[];
  readonly refsCruzadas: unknown;
  readonly observacao: string | null;
}

// -------------------------------------------------------- domínio do front

export type TipoDeRegra =
  | 'unidade_cobranca'
  | 'inclusao'
  | 'exclusao'
  | 'formula_preco'
  | 'autorizacao'
  | 'regra_cobranca'
  | 'prazo_documental'
  | 'sla'
  | 'hipotese_glosa'
  | 'prazo_recurso'
  | 'nao_classificavel';

export const TIPOS_DE_REGRA: readonly TipoDeRegra[] = [
  'nao_classificavel',
  'unidade_cobranca',
  'inclusao',
  'exclusao',
  'formula_preco',
  'autorizacao',
  'regra_cobranca',
  'prazo_documental',
  'sla',
  'hipotese_glosa',
  'prazo_recurso',
];

export const ROTULOS_DE_TIPO_DE_REGRA: Record<TipoDeRegra, string> = {
  unidade_cobranca: 'Unidade de cobrança',
  inclusao: 'Inclusão',
  exclusao: 'Exclusão',
  formula_preco: 'Fórmula de preço',
  autorizacao: 'Autorização',
  regra_cobranca: 'Regra de cobrança',
  prazo_documental: 'Prazo documental',
  sla: 'SLA',
  hipotese_glosa: 'Hipótese de glosa',
  prazo_recurso: 'Prazo de recurso',
  nao_classificavel: 'Não classificável',
};

export type ConfiancaDaRegra = 'alta' | 'media' | 'baixa';

export const ROTULOS_DE_CONFIANCA: Record<ConfiancaDaRegra, string> = {
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

export type EstadoDaRegra = 'extraida' | 'aprovada' | 'ajustada' | 'rejeitada' | 'aposentada';

export const ROTULOS_DE_ESTADO_DA_REGRA: Record<EstadoDaRegra, string> = {
  extraida: 'Extraída',
  aprovada: 'Aprovada',
  ajustada: 'Ajustada',
  rejeitada: 'Rejeitada',
  aposentada: 'Aposentada',
};

export type MotivoDoDiffDoItem = 'entrou' | 'saiu' | 'mudou';

/** Em palavras de quem lê: "saiu" soa como retirada do contrato, e o diff só vê o que este documento não cita. */
export const ROTULOS_DO_MOTIVO_DO_DIFF: Record<MotivoDoDiffDoItem, string> = {
  entrou: 'Novo',
  saiu: 'Não aparece',
  mudou: 'Valor alterado',
};

/** A dica ao passar o mouse: o motivo de cada selo, para a tela não precisar de parágrafo. */
export const DICAS_DO_MOTIVO_DO_DIFF: Record<MotivoDoDiffDoItem, string> = {
  entrou: 'O código existe neste documento e não existia no documento comparado.',
  saiu: 'O código existia no documento comparado e este não o cita.',
  mudou: 'O código está nos dois, mas o valor ou a unidade de cobrança é diferente.',
};

export interface RegraDaFila {
  readonly id: string;
  readonly fonteId: string;
  readonly tipoDeFonte: string | null;
  readonly tipo: TipoDeRegra;
  readonly confianca: ConfiancaDaRegra;
  readonly escopo: unknown;
  readonly efeito: unknown;
  readonly trecho: string | null;
  readonly pagina: number;
  readonly trechoLocalizado: boolean;
  readonly observacoes: string | null;
  /** Código → descrição do item no contrato; código ausente não tem item no contrato. */
  readonly descricoesDosItens: Readonly<Record<string, string>>;
}

export interface RegraRevisada {
  readonly estado: EstadoDaRegra;
  readonly novaRegraId: string | null;
}

/** Um lado (antes ou depois) de um item do diff: o que a pessoa precisa ver para aprovar a unidade. */
export interface LadoDoItemDoDiff {
  readonly id: string;
  readonly descricao: string;
  readonly unidadeDeCobranca: string;
  readonly valor: number;
  readonly pagina: number;
}

export interface ItemDoDiff {
  readonly codigo: string | null;
  readonly antes: LadoDoItemDoDiff | null;
  readonly depois: LadoDoItemDoDiff | null;
  readonly motivo: MotivoDoDiffDoItem;
}

export interface RegraAjustadaNoDiff {
  readonly regraId: string;
  readonly tipo: TipoDeRegra;
  readonly substituiRegraId: string;
}

/** Uma fonte identificada por tipo, datas, tamanho e o início do hash do arquivo. */
export interface ReferenciaDeFonte {
  readonly id: string;
  readonly tipo: TipoDeFonte;
  readonly criadaEm: string;
  readonly vigenciaIni: string;
  readonly paginas: number | null;
  readonly totalDeItens: number;
  readonly hash: string;
}

export interface DiffDaFonte {
  readonly primeiraFonte: boolean;
  /** A fonte sob revisão; `null` se a API não a devolveu. */
  readonly atual: ReferenciaDeFonte | null;
  /** A fonte ativa contra a qual se comparou; `null` na primeira fonte do contrato. */
  readonly anterior: ReferenciaDeFonte | null;
  readonly itens: readonly ItemDoDiff[];
  readonly regrasAjustadas: readonly RegraAjustadaNoDiff[];
}

export interface PaginaDaFonte {
  readonly pagina: number;
  readonly texto: string;
  readonly mascarado: boolean;
  readonly observacao: string | null;
}

/** As três etapas que `POST /fontes/{id}/reprocessar?etapa=` aceita (T11). */
export type EtapaDeReprocesso = 'mapa' | 'clausulas' | 'regras';

export const ETAPAS_DE_REPROCESSO: readonly EtapaDeReprocesso[] = ['mapa', 'clausulas', 'regras'];

export const ROTULOS_DE_ETAPA: Record<EtapaDeReprocesso, string> = {
  mapa: 'Mapa do documento',
  clausulas: 'Cláusulas',
  regras: 'Regras',
};
